import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  List,
  // ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress,
  Alert,
  Chip,
  // Tooltip,
  Divider,
  alpha,
  useTheme,
  Paper,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  CameraAlt as CameraIcon,
  Thermostat as ThermostatIcon,
  Opacity as OpacityIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { backend_url } from "../App";

function SensorList({ sensorSelect }) {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openItems, setOpenItems] = useState({});

  const config = React.useMemo(
    () => ({
      headers: {
        Authorization: `Token ${localStorage.getItem("token")}`,
      },
    }),
    [],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${backend_url}/core/api/towers/`, config);
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        const actualData = await response.json();
        setData(actualData.results);
        setError(null);
      } catch (err) {
        setError(err.message);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [config]);

  const handleToggle = (index) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleCameraClick = useCallback(
    (camera) => {
      sensorSelect({
        ...camera,
        isCamera: true,
        user: true,
        location: {
          lat: camera.latitude,
          lng: camera.longitude,
        },
        id: camera.id,
        description: camera.description,
        latitude: camera.latitude,
        longitude: camera.longitude,
        live: camera.live,
        start_angle: camera.start_angle,
        end_angle: camera.end_angle,
        video_interval: camera.video_interval,
        name: camera.description,
        lat: camera.latitude,
        lng: camera.longitude,
      });
    },
    [sensorSelect],
  );

  const handleTowerClick = useCallback(
    (tower) => {
      sensorSelect({ ...tower, isTower: true });
    },
    [sensorSelect],
  );

  const handleSensorClick = useCallback(
    (sensor) => {
      sensorSelect({ ...sensor, isSensor: true });
    },
    [sensorSelect],
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: 400,
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Error loading devices: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        bgcolor: "background.paper",
        borderRadius: 5,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1,
          background: "linear-gradient(135deg, #454545 0%, #2d2d2d 100%)",
          color: "white",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, ml: 1 }}>
          Cameras & Sensors
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8, ml: 1 }}>
          Select a device to view details
        </Typography>
      </Box>

      <Divider />

      {/* List */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 0 }}>
        {data && data.length > 0 ? (
          data.map((item, index) => (
            <Box key={index} sx={{ mb: 0 }}>
              <ListItemButton
                onClick={() => handleTowerClick(item)}
                sx={{
                  borderRadius: 2,
                  // mb: 0.1,
                  bgcolor: openItems[index]
                    ? alpha(theme.palette.primary.main, 0.08)
                    : "transparent",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <ListItemIcon>
                  <LocationIcon sx={{ color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: "0.8rem",
                  }}
                  secondary={`${item.cameras?.length || 0} cameras, ${item.sensors?.length || 0} sensors`}
                  secondaryTypographyProps={{
                    fontSize: "0.7rem",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(index);
                  }}
                >
                  {openItems[index] ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </ListItemButton>

              <Collapse in={openItems[index]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 1 }}>
                  {/* Cameras Section */}
                  {item.cameras && item.cameras.length > 0 && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          px: 2,
                          pt: 1,
                          display: "block",
                          color: "text.secondary",
                          fontWeight: 600,
                        }}
                      >
                        📷 CAMERAS
                      </Typography>
                      {item.cameras.map(
                        (camera, camIndex) =>
                          camera.live && (
                            <ListItemButton
                              key={camIndex}
                              onClick={() => handleCameraClick(camera)}
                              sx={{
                                borderRadius: 2,
                                mb: 0.1,
                                pl: 3,
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.04,
                                  ),
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CameraIcon
                                  sx={{
                                    fontSize: 20,
                                    color: theme.palette.info.main,
                                  }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={camera.description}
                                primaryTypographyProps={{
                                  fontSize: "0.85rem",
                                }}
                                secondary={
                                  <Box
                                    component="span"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      mt: 0.5,
                                    }}
                                  >
                                    <Chip
                                      label={camera.live ? "LIVE" : "OFFLINE"}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: "0.65rem",
                                        fontWeight: 600,
                                        bgcolor: camera.live
                                          ? alpha(
                                              theme.palette.success.main,
                                              0.15,
                                            )
                                          : alpha(
                                              theme.palette.error.main,
                                              0.15,
                                            ),
                                        color: camera.live
                                          ? theme.palette.success.main
                                          : theme.palette.error.main,
                                      }}
                                    />
                                    {camera.start_angle !== undefined && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {camera.start_angle}° →{" "}
                                        {camera.end_angle}°
                                      </Typography>
                                    )}
                                  </Box>
                                }
                                secondaryTypographyProps={{ component: "span" }}
                              />
                            </ListItemButton>
                          ),
                      )}
                    </>
                  )}

                  {/* Sensors Section */}
                  {item.sensors && item.sensors.length > 0 && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          px: 2,
                          py: 1,
                          display: "block",
                          color: "text.secondary",
                          fontWeight: 600,
                          mt: 1,
                        }}
                      >
                        📊 SENSORS
                      </Typography>
                      {item.sensors.map((sensor, sensorIndex) => (
                        <ListItemButton
                          key={sensorIndex}
                          onClick={() => handleSensorClick(sensor)}
                          sx={{
                            borderRadius: 2,
                            mb: 0.5,
                            pl: 3,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {sensor.sensor_type === "Temperature" ? (
                              <ThermostatIcon
                                sx={{
                                  fontSize: 20,
                                  color: theme.palette.warning.main,
                                }}
                              />
                            ) : (
                              <OpacityIcon
                                sx={{
                                  fontSize: 20,
                                  color: theme.palette.info.main,
                                }}
                              />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={sensor.name}
                            primaryTypographyProps={{
                              fontSize: "0.85rem",
                            }}
                            secondary={sensor.sensor_type}
                            secondaryTypographyProps={{
                              fontSize: "0.7rem",
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </>
                  )}
                </List>
              </Collapse>
            </Box>
          ))
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No devices found
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// Small helper component for the icon button
const IconButton = ({ size, onClick, children, ...props }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      background: "none",
      border: "none",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 0.5,
      borderRadius: 1,
      color: "text.secondary",
      "&:hover": {
        bgcolor: "action.hover",
      },
    }}
    {...props}
  >
    {children}
  </Box>
);

export default SensorList;
